import { SketchElement, isLayerElement } from '../../../inspector'
import { FlatTreeNode } from './types'

/**
 * Find all ancestor identifiers for a given element by walking up the parent chain
 */
export function findAncestorIdentifiers(element: SketchElement): bigint[] {
  const ancestors: bigint[] = []
  let current = element.parent

  while (current) {
    ancestors.push(current.identifier)
    current = current.parent
  }

  return ancestors
}

/**
 * Flatten the tree structure into a list removing collapsed layers
 */
export function flattenTree(
  elements: SketchElement[],
  collapsedElements: Set<bigint>,
  level = 0
): FlatTreeNode[] {
  const result: FlatTreeNode[] = elements.reduce<FlatTreeNode[]>(
    (acc, element) => {
      const hasChildren = element.children.length > 0
      const isCollapsed = collapsedElements.has(element.identifier)
      const isLayerMasked = isLayerElement(element)
        ? element.isLayerMasked
        : false

      acc.push({
        sketchElement: element,
        level,
        hasChildren,
        isCollapsed,
        isLayerMasked,
      })

      // Recursively add children if not collapsed
      if (hasChildren && !isCollapsed) {
        acc.push(...flattenTree(element.children, collapsedElements, level + 1))
      }

      return acc
    },
    []
  )

  return result
}

/**
 * Filter tree based on search query - returns a new tree with only elements that match
 * or have matching descendants
 */
export function filterTree(
  elements: SketchElement[],
  searchQuery: string
): SketchElement[] {
  if (!searchQuery) return elements

  const query = searchQuery.toLowerCase()

  return elements.reduce<SketchElement[]>((acc, element) => {
    const matches = element.name.toLowerCase().includes(query)
    const filteredChildren = filterTree(element.children, searchQuery)

    // Include element if it matches or has matching children
    if (matches || filteredChildren.length > 0) {
      acc.push({
        ...element,
        children: filteredChildren,
      })
    }

    return acc
  }, [])
}
